<template>
  <div class="rrd-statistics-page">
    <div class="rrd-sticky-top">
      <div class="rrd-sticky-head-row px-2 mb-2">
        <div class="rrd-head-slot rrd-head-slot--left" aria-hidden="true"></div>
        <h3 class="rrd-title m-0 rrd-head-title">Statistics of Qualifications</h3>
        <div class="rrd-head-slot rrd-head-slot--right" aria-hidden="true"></div>
      </div>

      <div class="rrd-filter-card">
        <div class="row align-items-end">
          <div class="col-md-5 col-lg-4 mb-0">
            <label class="rrd-filter-label">Year</label>

            <div class="d-flex">
              <select v-model="filter.year" class="form-control rrd-filter-input mr-2">
                <option :value="null">All</option>
                <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
              </select>

              <button class="btn rrd-filter-btn" :disabled="isLoading" @click="onShow">
                <template v-if="isLoading">
                  <i class="fa fa-spinner "></i>
                </template>
                <template v-else>
                  <i class="fa fa-search mr-1"/>
                </template>
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="rrd-scroll-body">
      <!-- SUMMARY BAR -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="rrd-summary-card">
            <div class="rrd-summary-grid">
              <div class="rrd-metric">
                <div class="rrd-metric-label">Applications</div>
                <div class="rrd-metric-value">{{ totals.applications }}</div>
              </div>

              <div class="rrd-metric">
                <div class="rrd-metric-label">Positive decisions</div>
                <div class="rrd-metric-value">
                  {{ totals.positive }}
                  <span class="rrd-metric-sub" v-if="totals.applications > 0">
                    ({{ pct(totals.positive, totals.applications) }}%)
                  </span>
                </div>
              </div>

              <div class="rrd-metric">
                <div class="rrd-metric-label">Bachelor</div>
                <div class="rrd-metric-value">
                  {{ totals.bachelorReceived }}
                  <span class="rrd-metric-sub">received</span>
                  <span class="rrd-metric-sub">/ {{ totals.bachelorPositive }} positive</span>
                </div>
              </div>

              <div class="rrd-metric">
                <div class="rrd-metric-label">Master</div>
                <div class="rrd-metric-value">
                  {{ totals.masterReceived }}
                  <span class="rrd-metric-sub">received</span>
                  <span class="rrd-metric-sub">/ {{ totals.masterPositive }} positive</span>
                </div>
              </div>
              <div class="rrd-metric">
                <div class="rrd-metric-label">Phd</div>
                <div class="rrd-metric-value">
                  {{ totals.phdReceived }}
                  <span class="rrd-metric-sub">received</span>
                  <span class="rrd-metric-sub">/ {{ totals.phdPositive }} positive</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- CHART (lightweight stacked bars, no extra deps) -->
      <div class="rrd-card mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="rrd-card-title">Applications by economy</div>
        </div>

        <div v-if="economiesForChart.length" class="rrd-bars">
          <div
            v-for="row in economiesForChart"
            :key="row.economy_name"
            class="rrd-bar-row"
          >
            <div class="rrd-bar-name">
              {{ row.economy_name }}
            </div>

            <div class="rrd-bar-wrap">
              <div class="rrd-bar-track" :title="barTitle(row)">
                <div
                  class="rrd-bar-seg rrd-bar-bachelor"
                  :style="{ width: segWidth(row, 'bachelor') }"
                />
                <div
                  class="rrd-bar-seg rrd-bar-master"
                  :style="{ width: segWidth(row, 'master') }"
                />
                <div
                  class="rrd-bar-seg rrd-bar-phd"
                  :style="{ width: segWidth(row, 'phd') }"
                />
              </div>

              <div class="rrd-bar-meta">
                <span class="rrd-pill">{{ rowTotal(row) }} apps</span>
                <span class="rrd-pill rrd-pill-soft">
                  {{ positiveTotal(row) }} positive ({{ pct(positiveTotal(row), Math.max(rowTotal(row), 1)) }}%)
                </span>
              </div>
            </div>
          </div>

          <div class="rrd-legend mt-3">
            <span class="rrd-legend-item"><i class="rrd-dot rrd-dot-bachelor"></i> Bachelor</span>
            <span class="rrd-legend-item"><i class="rrd-dot rrd-dot-master"></i> Master</span>
            <span class="rrd-legend-item"><i class="rrd-dot rrd-dot-phd"></i> PhD</span>
          </div>
        </div>

        <div v-else class="rrd-empty">
          No data to display.
        </div>
      </div>

      <!-- ECONOMY CARDS -->
      <div class="row">
        <div
          v-for="row in statisticData"
          :key="row.economy_name"
          class="col-12 col-lg-6 mb-3"
        >
          <div class="rrd-economy-card">
            <div class="d-flex justify-content-between align-items-start">
              <div class="rrd-economy-title">{{ row.economy_name }}</div>
              <div class="rrd-economy-kpi">
                <span class="rrd-pill">{{ rowTotal(row) }} apps</span>
              </div>
            </div>

            <div class="rrd-economy-sub">
              Positive decisions:
              <strong>{{ positiveTotal(row) }}</strong>
              <span class="rrd-muted">({{ pct(positiveTotal(row), Math.max(rowTotal(row), 1)) }}%)</span>
            </div>

            <div class="rrd-progress">
              <div class="rrd-progress-track">
                <div
                  class="rrd-progress-fill"
                  :style="{ width: pct(positiveTotal(row), Math.max(rowTotal(row), 1)) + '%' }"
                />
              </div>
            </div>

            <div class="rrd-levels">
              <div class="rrd-level">
                <div class="rrd-level-label">🎓 Bachelor</div>
                <div class="rrd-level-value">
                  {{ getNum(row, 'bachelor_received') }} received
                  <span class="rrd-muted">/ {{ getNum(row, 'bachelor_positive_recognition') }} positive</span>
                </div>
              </div>

              <div class="rrd-level">
                <div class="rrd-level-label">🎓 Master</div>
                <div class="rrd-level-value">
                  {{ getNum(row, 'master_received') }} received
                  <span class="rrd-muted">/ {{ getNum(row, 'master_positive_recognition') }} positive</span>
                </div>
              </div>

              <div class="rrd-level">
                <div class="rrd-level-label">🎓 PhD</div>
                <div class="rrd-level-value">
                  {{ getNum(row, 'phd_received') }} received
                  <span class="rrd-muted">/ {{ getNum(row, 'phd_positive_recognition') }} positive</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { getStatistics } from "@/services/public";

export default {
  name: "Statistic",
  data() {
    const currentYear = new Date().getFullYear();
    const startYear = 2022;
    const years = Array.from(
      { length: currentYear - startYear + 1 },
      (_, i) => startYear + i
    );
    return {
      isLoading: false,
      years: years,
      filter: {
        year: null,
      },
      statisticData: [],
    };
  },

  computed: {
    resultsCount() {
      // original UI shows "qualifications found" – ovdje uzimam broj ekonomija (redova)
      // ako API ima total qualifications count, samo zamijeni ovom vrijednošću
      return Array.isArray(this.statisticData) ? this.statisticData.length : 0;
    },

    totals() {
      const rows = Array.isArray(this.statisticData) ? this.statisticData : [];

      const sum = (key) =>
        rows.reduce((acc, r) => acc + this.getNum(r, key), 0);

      return {
        applications: sum("total_recived"),
        positive: sum("positive_recognition"),

        bachelorReceived: sum("bachelor_received"),
        bachelorPositive: sum("bachelor_positive_recognition"),

        masterReceived: sum("master_received"),
        masterPositive: sum("master_positive_recognition"),

        phdReceived: sum("phd_received"),
        phdPositive: sum("phd_positive_recognition"),
      };
    },

    maxTotalForChart() {
      const rows = Array.isArray(this.statisticData) ? this.statisticData : [];
      let max = 0;
      for (const r of rows) {
        const t = this.rowTotal(r);
        if (t > max) max = t;
      }
      return Math.max(max, 1);
    },

    economiesForChart() {
      const rows = Array.isArray(this.statisticData) ? [...this.statisticData] : [];
      // sort desc by total apps for nicer chart
      rows.sort((a, b) => this.rowTotal(b) - this.rowTotal(a));
      return rows;
    },
  },

  methods: {
    async onShow() {
      await this.getTableData({ year: this.filter.year });
    },

    async getTableData(request) {
      this.isLoading = true;
      try {
        const response = await getStatistics(request);
        this.statisticData = response.data.economy_data_list || [];
      } catch (e) {
        this.statisticData = [];
      } finally {
        this.isLoading = false;
      }
    },

    // helpers
    getNum(row, key) {
      // supports your nested structure: row.statistics.<key>
      const v =
        row &&
        row.statistics &&
        Object.prototype.hasOwnProperty.call(row.statistics, key)
          ? row.statistics[key]
          : 0;

      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    },

    rowTotal(row) {
      // prefer total_received if present, else sum by levels
      const total = this.getNum(row, "total_recived");
      if (total > 0) return total;

      return (
        this.getNum(row, "bachelor_received") +
        this.getNum(row, "master_received") +
        this.getNum(row, "phd_received")
      );
    },

    positiveTotal(row) {
      const total = this.getNum(row, "positive_recognition");
      if (total > 0) return total;

      return (
        this.getNum(row, "bachelor_positive_recognition") +
        this.getNum(row, "master_positive_recognition") +
        this.getNum(row, "phd_positive_recognition")
      );
    },

    pct(part, total) {
      const p = Number(part) || 0;
      const t = Number(total) || 0;
      if (!t) return 0;
      return Math.round((p / t) * 100);
    },

    segWidth(row, level) {
      const t = this.maxTotalForChart;

      let val = 0;
      if (level === "bachelor") val = this.getNum(row, "bachelor_received");
      if (level === "master") val = this.getNum(row, "master_received");
      if (level === "phd") val = this.getNum(row, "phd_received");

      const w = (val / t) * 100;
      return `${Math.max(0, Math.min(100, w))}%`;
    },

    barTitle(row) {
      return (
        `${row.economy_name}\n` +
        `Bachelor: ${this.getNum(row, "bachelor_received")} (positive ${this.getNum(row, "bachelor_positive_recognition")})\n` +
        `Master: ${this.getNum(row, "master_received")} (positive ${this.getNum(row, "master_positive_recognition")})\n` +
        `PhD: ${this.getNum(row, "phd_received")} (positive ${this.getNum(row, "phd_positive_recognition")})`
      );
    },
  },

  async mounted() {
    // initial load
    await this.getTableData({ year: this.filter.year });
  },
};
</script>

